import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import {
  getAdminMerchantsGetAdminMerchantsIdGuidQueryKey,
  getAdminMerchantsGetAdminMerchantsQueryKey,
  useAdminMerchantsGetAdminMerchantsIdGuid,
  useAdminMerchantsPutAdminMerchantsIdGuid,
} from '@/lib/api/admin-merchants/admin-merchants';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const Schema = z.object({
  merchantId: z.string().min(1).max(64).optional().or(z.literal('')),
  name: z.string().max(200).optional().or(z.literal('')),
  balanceAccountId: z.string().max(64).optional().or(z.literal('')),
});
type Form = z.infer<typeof Schema>;

interface Props {
  merchantId: string | null;
  onOpenChange: (open: boolean) => void;
}

export function EditMerchantDialog({ merchantId, onOpenChange }: Props) {
  const open = merchantId !== null;
  const queryClient = useQueryClient();

  const detailQuery = useAdminMerchantsGetAdminMerchantsIdGuid(merchantId ?? '', {
    query: { enabled: open, staleTime: 0 },
  });

  const mutation = useAdminMerchantsPutAdminMerchantsIdGuid({
    mutation: {
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: getAdminMerchantsGetAdminMerchantsQueryKey() }),
          merchantId
            ? queryClient.invalidateQueries({
                queryKey: getAdminMerchantsGetAdminMerchantsIdGuidQueryKey(merchantId),
              })
            : Promise.resolve(),
        ]);
        toast.success('Merchant updated.');
        onOpenChange(false);
      },
    },
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting, isDirty },
  } = useForm<Form>({
    resolver: zodResolver(Schema),
    defaultValues: { merchantId: '', name: '', balanceAccountId: '' },
  });

  useEffect(() => {
    if (!detailQuery.data) return;
    reset({
      merchantId: detailQuery.data.merchantId,
      name: detailQuery.data.name,
      balanceAccountId: detailQuery.data.balanceAccountId ?? '',
    });
  }, [detailQuery.data, reset]);

  const onSubmit = handleSubmit(async (values) => {
    if (!merchantId) return;
    try {
      await mutation.mutateAsync({
        id: merchantId,
        data: {
          merchantId: values.merchantId || null,
          name: values.name || null,
          balanceAccountId: values.balanceAccountId || null,
        },
      });
    } catch (err) {
      const status = (err as { response?: { status?: number } }).response?.status;
      toast.error(
        status === 409 ? 'A merchant with that id already exists under this client.' : 'Could not update merchant.',
      );
    }
  });

  return (
    <Dialog open={open} onOpenChange={(next) => (next ? undefined : onOpenChange(false))}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit merchant</DialogTitle>
          <DialogDescription>
            {detailQuery.data ? `Editing ${detailQuery.data.name}.` : 'Loading…'}
          </DialogDescription>
        </DialogHeader>

        {detailQuery.isLoading ? (
          <div className="py-12 text-center text-sm text-muted-foreground">Loading…</div>
        ) : !detailQuery.data ? (
          <div className="py-12 text-center text-sm text-destructive">Merchant not found.</div>
        ) : (
          <form onSubmit={onSubmit} className="space-y-4" noValidate>
            <div className="space-y-2">
              <Label htmlFor="edit-merchant-code">Merchant ID (Adyen merchant-account code)</Label>
              <Input id="edit-merchant-code" autoComplete="off" {...register('merchantId')} />
              {errors.merchantId && <p className="text-xs text-destructive">{errors.merchantId.message}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-merchant-name">Name</Label>
              <Input id="edit-merchant-name" {...register('name')} />
              {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-balance">Balance Account ID (optional)</Label>
              <Input id="edit-balance" placeholder="BA32..." autoComplete="off" {...register('balanceAccountId')} />
            </div>

            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isSubmitting || mutation.isPending || !isDirty}>
                {mutation.isPending ? 'Saving…' : 'Save changes'}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
